import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { FaServer, FaCheckCircle, FaExclamationTriangle, FaHdd, FaUsers, FaArrowRight, FaClock, FaBug, FaSync, FaExpand, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import UserDetailModal from "../components/UserDetailModal";
import NodeDetailModal from "../components/NodeDetailModal";
import ExpandedChartModal from "../components/ExpandedChartModal";
import TenantTabs from "../components/TenantTabs";
import TimeFilter from "../components/TimeFilter";
import InfoTooltip from "../components/InfoTooltip";
import LoadingOverlay from "../components/LoadingOverlay";
import ScrollableTable from "../components/ScrollableTable";
import { getOperationalInsights, getAvailablePlans } from "../services/analyticsService";
import { useTimeFilter } from "../context/TimeFilterContext";
import { getNodeTitle } from "../utils/nodeMappings";

/**
 * @file OperationalAnalytics.js
 * @description Detailed analytics dashboard for system performance, execution trends, and node metrics.
 */
export default function OperationalAnalytics() {
  const { timeFilter, setTimeFilter, customRange, handleCustomRangeChange,
    planFilter, setPlanFilter,
    availablePlans, setAvailablePlans
  } = useTimeFilter();

  console.log("[OperationalAnalytics] RENS: timeFilter =", timeFilter);

  const [activeTab, setActiveTab] = useState("estrel"); // estrel, education, enterprise
  const [showModal, setShowModal] = useState(false);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [data, setData] = useState(null);

  const [nodeSortConfig, setNodeSortConfig] = useState({ key: 'executions', direction: 'desc' });
  const [tenantSortConfig, setTenantSortConfig] = useState({ key: 'total_executions', direction: 'desc' });
  const [tenantSearchQuery, setTenantSearchQuery] = useState("");

  const handleExpandChart = (title, options, series, type) => {
    setExpandedChart({ title, options, series, type });
  };

  const ITEMS_PER_PAGE = 10;
  const [nodePage, setNodePage] = useState(1);
  const [tenantPage, setTenantPage] = useState(1);

  // Reset pagination when data changes
  useEffect(() => {
    setNodePage(1);
    setTenantPage(1);
  }, [data, activeTab, timeFilter]);

  useEffect(() => {
    // Guard clause for incomplete custom range
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
    getAvailablePlans(timeFilter, customRange).then(setAvailablePlans);
  }, [timeFilter, customRange]);

  // Safe extraction for Hooks
  const tenants = data?.tenantMetrics || [];
  const nodeMetrics = data?.nodeMetrics || [];

  // Filter Tenants based on Search Query (Moved before conditional return)
  const filteredTenants = React.useMemo(() => {
    let filtered = tenants;
    if (tenantSearchQuery) {
      const q = tenantSearchQuery.toLowerCase();
      filtered = filtered.filter((t) =>
        Object.values(t).some((val) =>
          String(val).toLowerCase().includes(q)
        )
      );
    }
    return filtered;
  }, [tenants, tenantSearchQuery]);

  // Restore loadData function
  const loadData = (force = false) => {
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) {
      return;
    }
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
    setLoading(true);

    if (force) {
      getAvailablePlans(timeFilter, customRange, true).then(setAvailablePlans);
    }

    getOperationalInsights(timeFilter, customRange, planFilter, force).then((res) => {
      setData(res);
      setLastUpdated(new Date());

      // Delay to allow charts to render
      setTimeout(() => {
        setLoading(false);
      }, 600);
    });
  };

  // Restore useEffect for data fetching
  useEffect(() => {
    loadData();
  }, [timeFilter, customRange, planFilter]);

  // Loading / Error States (Now placed AFTER all Hooks)
  if (!data && loading) {
    return (
      <div className="p-4 bg-light min-vh-100 position-relative">
        <LoadingOverlay loading={true} message="Initializing Analytics..." />
      </div>
    );
  }

  if (!data) return null;

  const kpis = data.kpis;

  // --- CHARTS CONFIG ---
  const volumeOptions = {
    chart: { type: "area", toolbar: { show: false } },
    stroke: { curve: "smooth", width: 2 },
    colors: ["#AD2D45"],
    fill: { type: "gradient", gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
    dataLabels: { enabled: false },
    xaxis: { categories: data.executionsTrend.labels },
    markers: { size: 4, hover: { size: 6 } }, // Ensure points are visible
  };

  const successRateOptions = {
    chart: { type: "line", toolbar: { show: false } },
    stroke: { curve: "monotoneCubic", width: 3 },
    colors: ["#10b981"],
    yaxis: { max: 100 },
    xaxis: { categories: data.successRate.labels },
    markers: { size: 4, hover: { size: 6 } }, // Ensure points are visible
    // annotations: {
    //   yaxis: [{ y: 95, borderColor: "#10b981", label: { text: "SLA (95%)", style: { color: "#fff", background: "#10b981" } } }]
    // }
  };

  const latencyOptions = {
    chart: { type: "line", toolbar: { show: false } },
    stroke: { width: [3, 2], dashArray: [0, 5] },
    colors: ["#912059", "#E68F17"],
    xaxis: { categories: data.workflowLatency.labels },
    legend: { position: "top" },
    markers: { size: 4, hover: { size: 6 } }, // Ensure points are visible
  };

  const donutOptions = {
    labels: data.errorDistribution.labels,
    colors: ["#E68F17", "#AD2D45", "#F4D677", "#912059"],
    legend: { position: "bottom" },
    plotOptions: { pie: { donut: { size: "70%" } } }
  };

  // Prepare Sorted Failure Data (Descending)
  const failureIndices = data.failuresByModule.series[0].data.map((_, i) => i);
  failureIndices.sort((a, b) => data.failuresByModule.series[0].data[b] - data.failuresByModule.series[0].data[a]);

  const sortedFailureLabels = failureIndices.map(i => getNodeTitle(data.failuresByModule.labels[i]));
  const sortedFailureSeries = [{
    name: data.failuresByModule.series[0].name || "Failures",
    data: failureIndices.map(i => data.failuresByModule.series[0].data[i])
  }];

  // Debug Log
  console.log("Raw Node Names:", data.failuresByModule.labels);
  console.log("Mapped Node Names:", sortedFailureLabels);

  const failureBarOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 4, horizontal: true } },
    colors: ["#AD2D45"],
    xaxis: { categories: sortedFailureLabels }
  };

  // --- CHARTS CONFIG ---


  const handleRowClick = (tenant) => {
    setSelectedTenant(tenant);
    setShowModal(true);
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setShowNodeModal(true);
  };


  // --- SORTING HELPERS ---


  const handleNodeSort = (key) => {
    let direction = 'asc';
    if (nodeSortConfig.key === key && nodeSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setNodeSortConfig({ key, direction });
  };

  const handleTenantSort = (key) => {
    let direction = 'asc';
    if (tenantSortConfig.key === key && tenantSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTenantSortConfig({ key, direction });
  };



  const sortedTenants = [...filteredTenants].sort((a, b) => {
    if (!tenantSortConfig.key) return 0;

    let aVal = a[tenantSortConfig.key];
    let bVal = b[tenantSortConfig.key];

    // Numeric conversions
    if (['projects', 'workflows', 'total_executions'].includes(tenantSortConfig.key)) {
      aVal = parseInt(aVal, 10) || 0;
      bVal = parseInt(bVal, 10) || 0;
    } else if (['execution_success_rate'].includes(tenantSortConfig.key)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else {
      aVal = (aVal || '').toString().toLowerCase();
      bVal = (bVal || '').toString().toLowerCase();
    }

    if (aVal < bVal) return tenantSortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return tenantSortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const sortedNodeMetrics = [...nodeMetrics].sort((a, b) => {
    if (!nodeSortConfig.key) return 0;

    let aVal = a[nodeSortConfig.key];
    let bVal = b[nodeSortConfig.key];

    // Numeric conversions for specific columns
    if (['executions', 'retries', 'error_count'].includes(nodeSortConfig.key)) {
      aVal = parseInt(aVal, 10) || 0;
      bVal = parseInt(bVal, 10) || 0;
    } else if (['success_rate', 'avg_latency', 'timeout_rate'].includes(nodeSortConfig.key)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else {
      // String comparison
      aVal = (aVal || '').toString().toLowerCase();
      bVal = (bVal || '').toString().toLowerCase();
    }

    if (aVal < bVal) return nodeSortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return nodeSortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });


  // --- PAGINATION HELPERS ---
  const nodeStartIndex = (nodePage - 1) * ITEMS_PER_PAGE;
  // Use sorted metrics for pagination
  const visibleNodeMetrics = sortedNodeMetrics.slice(nodeStartIndex, nodeStartIndex + ITEMS_PER_PAGE);
  const totalNodePages = Math.ceil(nodeMetrics.length / ITEMS_PER_PAGE);

  const tenantStartIndex = (tenantPage - 1) * ITEMS_PER_PAGE;
  // const visibleTenants = sortedTenants.slice(tenantStartIndex, tenantStartIndex + ITEMS_PER_PAGE);
  const totalTenantPages = Math.ceil(sortedTenants.length / ITEMS_PER_PAGE);

  const PaginationControls = ({ page, totalPages, setPage }) => (
    <div className="d-flex justify-content-end align-items-center gap-2 p-3 bg-white border-top">
      <button
        className="btn btn-sm btn-outline-secondary border-0"
        disabled={page === 1}
        onClick={() => setPage(p => Math.max(1, p - 1))}
      >
        <FaChevronLeft size={12} />
      </button>
      <span className="text-muted small fw-medium">
        {page} / {totalPages || 1}
      </span>
      <button
        className="btn btn-sm btn-outline-secondary border-0"
        disabled={page >= totalPages}
        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
      >
        <FaChevronRight size={12} />
      </button>
    </div>
  );

  const renderSortArrow = (key) => {
    if (nodeSortConfig.key !== key) return <span className="text-muted ms-1" style={{ fontSize: '0.7em' }}>↕</span>;
    return nodeSortConfig.direction === 'asc' ? <span className="ms-1">↑</span> : <span className="ms-1">↓</span>;
  };

  const renderTenantSortArrow = (key) => {
    if (tenantSortConfig.key !== key) return <span className="text-muted ms-1" style={{ fontSize: '0.7em' }}>↕</span>;
    return tenantSortConfig.direction === 'asc' ? <span className="ms-1">↑</span> : <span className="ms-1">↓</span>;
  };

  const tenantColumns = [
    {
      header: "Tenant",
      accessor: "tenant",
      render: (v) => <span className="text-dark">{v}</span>
    },
    {
      header: "Projects",
      accessor: "projects",
      align: 'center',
      render: (v) => <div>{v}</div>
    },
    {
      header: "Workflows",
      accessor: "workflows",
      align: 'center',
      render: (v) => <div>{v}</div>
    },
    {
      header: "Executions",
      accessor: "total_executions",
      align: 'center',
      render: (v) => <div>{v}</div>
    },
    {
      header: "Success Rate",
      accessor: "execution_success_rate",
      align: 'center',
      render: (v) => <div><span className="badge bg-light text-dark border">{v}</span></div>
    }
  ];

  return (
    <div className="container-fluid p-4 bg-light min-vh-100 position-relative">
      <LoadingOverlay loading={loading} />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-0">Usage Analytics</h2>
          <p className="text-muted small mb-0">Where is the platform being stressed, and at which layer?</p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm shadow-sm border-secondary-subtle"
            style={{ width: "130px", fontWeight: "500" }}
            value={planFilter || ""}
            onChange={(e) =>
              setPlanFilter(e.target.value === "" ? null : e.target.value)
            }
          >
            <option value="">All Plans</option>
            {availablePlans.map((p) => (
              <option key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </option>
            ))}
          </select>
          <TimeFilter
            value={timeFilter}
            onChange={setTimeFilter}
            startDate={customRange.start}
            endDate={customRange.end}
            onCustomRangeChange={handleCustomRangeChange}
          />
          <div className="text-end d-none d-md-block ms-2">
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>Last Update</div>
            <div className="fw-bold small">{lastUpdated ? lastUpdated.toLocaleTimeString() : "-"}</div>
          </div>
          <button className="btn btn-white border shadow-sm btn-sm px-3" onClick={() => loadData(true)} disabled={loading}>
            <FaSync className={loading ? "fa-spin me-2" : "me-2"} /> Refresh
          </button>
        </div>
      </div>

      {/* TABS */}
      <TenantTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* KPI GRID */}
      <div className="row g-3 mb-4">
        {[
          { label: "Total Executions", val: kpis.exec, icon: FaServer, color: "text-primary", tip: "Total Executions made." },
          { label: "Success Rate", val: `${kpis.successRate}%`, icon: FaCheckCircle, color: "text-success", tip: "Successful exceution/ Total exceutions." },
          { label: "Failed Executions", val: kpis.failed, icon: FaExclamationTriangle, color: "text-danger", tip: "Total failed runs." },
          { label: "Avg Latency", val: `${(kpis.latency / 1000).toFixed(2)}s`, icon: FaClock, color: "text-warning", tip: "Average run duration." },
          { label: "Failure Rate", val: kpis.failureRate, icon: FaBug, color: "text-danger", tip: "Failed exceution/ Total exceutions" },
          { label: "Storage Used", val: kpis.storage, icon: FaHdd, color: "text-secondary", tip: "Total vector/file storage." },
          { label: "Total Users", val: kpis.users, icon: FaUsers, color: "text-info", tip: "Unique active users." },
        ].map((k, i) => (
          <div key={i} className="col-xl-3 col-md-4 col-sm-6">
            <div className="card border-0 shadow-sm h-100 p-3">
              <div className="d-flex align-items-center gap-3">
                <div className={`p-3 rounded bg-light ${k.color}`}><k.icon size={20} /></div>
                <div>
                  <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>
                    {k.label} <InfoTooltip text={k.tip} />
                  </div>
                  <h4 className="fw-bold mb-0">{k.val}</h4>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CHARTS ROW 1 */}
      <div className="row g-4 mb-4">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">Execution Volume Trend <InfoTooltip text="Visualizes the total number of  executions processed over the selected time period." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Execution Volume Trend", volumeOptions, data.executionsTrend.series, "area")}>
                <FaExpand />
              </button>
            </div>
            {data.executionsTrend.series[0].data.length > 0 ? (
              <Chart options={volumeOptions} series={data.executionsTrend.series} type="area" height={250} />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "250px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">Success Rate Trend <InfoTooltip text="Tracks the percentage of successful workflow executions over time. High success rates indicate system stability." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Success Rate Trend", successRateOptions, data.successRate.series, "line")}>
                <FaExpand />
              </button>
            </div>
            {data.successRate.series[0].data.length > 0 ? (
              <Chart options={successRateOptions} series={data.successRate.series} type="line" height={250} />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "250px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CHARTS ROW 2: Latency & Failures */}
      <div className="row g-4 mb-4">
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">Latency Trend (Avg vs P95) <InfoTooltip text="Compares average latency with P95 latency (95th percentile) to identify performance outliers." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Latency Trend", latencyOptions, data.workflowLatency.series, "line")}>
                <FaExpand />
              </button>
            </div>
            {data.workflowLatency.series[0].data.length > 0 ? (
              <Chart options={latencyOptions} series={data.workflowLatency.series} type="line" height={220} />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "220px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">Failure Type Distribution <InfoTooltip text="Breakdown of error categories (e.g., Timeout, API Error) to pinpoint root causes of failures." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Failure Type Distribution", donutOptions, data.errorDistribution.series, "donut")}>
                <FaExpand />
              </button>
            </div>
            {data.errorDistribution.series.length > 0 && data.errorDistribution.series.some(v => v > 0) ? (
              <Chart options={donutOptions} series={data.errorDistribution.series} type="donut" height={220} />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "220px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">Node Type Failure Breakdown <InfoTooltip text="Identifies which specific workflow nodes (e.g., LLM Node, API Tool) are causing the most failures." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Node Type Failure Breakdown", failureBarOptions, sortedFailureSeries, "bar")}>
                <FaExpand />
              </button>
            </div>
            {sortedFailureSeries[0].data.some(v => v > 0) ? (
              <Chart options={failureBarOptions} series={sortedFailureSeries} type="bar" height={220} />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "220px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>
      </div>

      {/* NODE METRICS TABLE */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-header bg-white py-3">
          <h6 className="fw-bold mb-0">Node Metrics <span className="text-muted small fw-normal ms-2">(Retrieval metrics identify data failures vs. generation failures)</span></h6>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0 text-sm">
            <thead className="bg-light">
              <tr className="text-muted small text-uppercase" style={{ cursor: 'pointer' }}>
                <th onClick={() => handleNodeSort('node_name')}>Node Name {renderSortArrow('node_name')}</th>
                {/* <th onClick={() => handleNodeSort('type')}>Type {renderSortArrow('type')}</th> */}
                <th className="text-center" onClick={() => handleNodeSort('executions')}>Executions {renderSortArrow('executions')}</th>
                <th className="text-center" onClick={() => handleNodeSort('success_rate')}>Success Rate {renderSortArrow('success_rate')}</th>
                <th className="text-center" onClick={() => handleNodeSort('avg_latency')}>Avg Latency {renderSortArrow('avg_latency')}</th>
                <th className="text-center" onClick={() => handleNodeSort('content_relevance')}>Context Relevance {renderSortArrow('content_relevance')}</th>
                <th className="text-center" onClick={() => handleNodeSort('retries')}>Retries {renderSortArrow('retries')}</th>
                <th className="text-center" onClick={() => handleNodeSort('timeout_rate')}>Timeout Rate {renderSortArrow('timeout_rate')}</th>
                <th className="pe-4 text-end" onClick={() => handleNodeSort('error_count')}>Errors {renderSortArrow('error_count')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleNodeMetrics.map((n, i) => (
                <tr key={i} onClick={() => handleNodeClick(n)} style={{ cursor: 'pointer' }}>
                  <td className="ps-4 text-dark">{getNodeTitle(n.node_name)}</td>
                  {/* <td><span className={`badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3`}>{n.type}</span></td> */}
                  <td className="text-center font-monospace">{n.executions}</td>
                  <td className="text-center"><span className={`badge ${parseFloat(n.success_rate) > 90 ? 'bg-success-subtle text-success' : `${parseFloat(n.success_rate) >= 50 ? 'bg-warning-subtle text-warning' : 'bg-danger-subtle text-danger'}`}`}>{n.success_rate}</span></td>
                  <td className="text-center font-monospace">{(parseFloat(n.avg_latency) / 1000).toFixed(2)}s</td>
                  <td className={`text-center ${n.content_relevance === 'N/A' || n.content_relevance === '-' ? 'text-muted' : 'text-success'}`}>{n.content_relevance}</td>
                  <td className="text-center">{n.retries}</td>
                  <td className="text-center">{n.timeout_rate}</td>
                  <td className="pe-4 text-end text-danger">{n.error_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls page={nodePage} totalPages={totalNodePages} setPage={setNodePage} />
      </div>

      {/* TENANT MATRIX TABLE */}
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <h6 className="fw-bold mb-0 text-uppercase">Tenant Performance Matrix</h6>
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center gap-1 bg-light rounded px-2 py-1">
              <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setTenantPage(prev => Math.max(prev - 1, 1))} disabled={tenantPage === 1} style={{ fontSize: '1.2rem', lineHeight: '1' }}><FaChevronLeft size={12} /></button>
              <span className="small text-muted mx-2" style={{ fontSize: '0.8rem', minWidth: '40px', textAlign: 'center' }}>
                {tenantPage} / {totalTenantPages || 1}
              </span>
              <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setTenantPage(prev => Math.min(prev + 1, Math.ceil(sortedTenants.length / ITEMS_PER_PAGE)))} disabled={tenantPage >= Math.ceil(sortedTenants.length / ITEMS_PER_PAGE)} style={{ fontSize: '1.2rem', lineHeight: '1' }}><FaChevronRight size={12} /></button>
            </div>
            <div className="ms-3 border-start ps-3" style={{ minWidth: "250px" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                value={tenantSearchQuery}
                onChange={(e) => { setTenantSearchQuery(e.target.value); setTenantPage(1); }} // Reset page on search
              />
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <ScrollableTable
            title=""
            data={sortedTenants.slice((tenantPage - 1) * ITEMS_PER_PAGE, tenantPage * ITEMS_PER_PAGE)}
            columns={tenantColumns}
            searchPlaceholder="Search tenant..."
            height={500}
            sortConfig={tenantSortConfig}
            onSort={handleTenantSort}
            searchQuery={tenantSearchQuery}
            onSearch={setTenantSearchQuery}
            hideSearch={true}
            onRowClick={handleRowClick}
          />
        </div>
      </div>

      <UserDetailModal show={showModal} data={selectedTenant} timeFilter={timeFilter} customRange={customRange} onClose={() => setShowModal(false)} />
      <NodeDetailModal show={showNodeModal} data={selectedNode} timeFilter={timeFilter} planFilter={activeTab} customRange={customRange} onClose={() => setShowNodeModal(false)} />
      <ExpandedChartModal
        show={!!expandedChart}
        onClose={() => setExpandedChart(null)}
        {...expandedChart}
      />
    </div >
  );
}
